import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SortIcon, SortableHeader } from "./SortControls";
import { KEYWORD_COLUMN_TOOLTIPS as TT } from "../keywordColumnTooltips";
import type { HeroKeywordsSortField } from "@/types";
import { HeroRankColorKey } from "@/components/shared/HeroRankColorKey";
import { HERO_RANK_BAND_CLASSES } from "@/components/keyword-landscape-output/RankingCell";

type SortDirection = "asc" | "desc";

interface KeywordBankTableHeaderProps {
  readOnly: boolean;
  /**
   * Which caller this header is serving. `landscape` drops Theme, Utilization and Source -- the
   * three columns a landscape's Keyword Analysis tab does not carry. Everything else is identical,
   * which is the point: the design reference renders one table for both screens
   * (`signal-v3/src/components/shared/KeywordTable.tsx`, `mode="bank"`).
   */
  variant?: "bank" | "landscape";
  /**
   * AUT-496. False only where the values cannot be there: the public share payload leaves the
   * SERP Match fields out, so the column would be dashes for every row forever.
   */
  showSerpMatch?: boolean;
  stickyStyle: React.CSSProperties;
  stickyGrid: string;
  onResizeStart: (e: React.PointerEvent<HTMLElement>) => void;
  onResizeMove: (e: React.PointerEvent<HTMLElement>) => void;
  onResizeEnd: (e: React.PointerEvent<HTMLElement>) => void;
  sortField: HeroKeywordsSortField;
  sortDirection: SortDirection;
  disableSortInteractions: boolean;
  allSelected: boolean;
  onSelectAll: (checked: boolean) => void;
  onSortChange: (field: HeroKeywordsSortField) => void;
}

export function KeywordBankTableHeader({
  readOnly,
  variant = "bank",
  showSerpMatch = true,
  stickyStyle,
  stickyGrid,
  onResizeStart,
  onResizeMove,
  onResizeEnd,
  sortField,
  sortDirection,
  disableSortInteractions,
  allSelected,
  onSelectAll,
  onSortChange,
}: KeywordBankTableHeaderProps) {
  const showThemeAndTags = variant === "bank";

  return (
    <TableHeader>
      <TableRow>
        <TableHead sticky style={stickyStyle} className="py-0 px-0">
          <div className={stickyGrid}>
            {!readOnly && (
              <span className="py-2.5 px-2 flex items-center justify-center">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={onSelectAll}
                  aria-label="Select all keywords on this page"
                />
              </span>
            )}
            <span className="py-2.5" />
            <span
              className="py-2.5 px-3 font-bold text-sm flex items-center gap-1.5 cursor-pointer select-none whitespace-nowrap"
              onClick={() => {
                if (disableSortInteractions) return;
                onSortChange("keyword");
              }}
            >
              {/* AUT-442: no hint here. The rank colour key this header used to carry describes
                  the Hero Rank column, and now sits on it. */}
              Keywords
              <SortIcon field="keyword" sortField={sortField} sortDirection={sortDirection} />
            </span>
          </div>
          {/* AUT-143: drag to resize the keyword column, Excel/Sheets-style. */}
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize keyword column"
            onPointerDown={onResizeStart}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeEnd}
            onPointerCancel={onResizeEnd}
            className={cn(
              "!absolute right-0 top-0 bottom-0 w-2 -mr-1 cursor-col-resize touch-none select-none z-10",
              "before:content-[''] before:absolute before:right-[3px] before:top-2 before:bottom-2 before:w-px before:bg-border",
              "hover:before:right-0 hover:before:top-0 hover:before:bottom-0 hover:before:w-full hover:before:bg-primary/40",
              "active:before:bg-primary/60 before:transition-all",
            )}
          />
        </TableHead>
        <TableHead
          className="w-[90px] py-2.5 px-2 text-center font-bold text-sm cursor-pointer select-none"
          onClick={() => {
            if (disableSortInteractions) return;
            onSortChange("heroRank");
          }}
        >
          <div className="flex items-center justify-center gap-1">
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger
                  className="cursor-help border-b border-dotted border-muted-foreground"
                  onClick={(e) => e.preventDefault()}
                >
                  Hero Rank
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[300px]">
                  <p>{TT.heroRank}</p>
                  <HeroRankColorKey swatches={HERO_RANK_BAND_CLASSES} />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="shrink-0">
              <SortIcon field="heroRank" sortField={sortField} sortDirection={sortDirection} />
            </span>
          </div>
        </TableHead>
        <SortableHeader
          field="kpg"
          label="KPS"
          tooltip={TT.kps}
          className="w-[64px]"
          sortField={sortField}
          sortDirection={sortDirection}
          disableSortInteractions={disableSortInteractions}
          onSortChange={onSortChange}
        />
        <SortableHeader
          field="keywordSales"
          label="KW Sales"
          tooltip={TT.kwSales}
          className="w-[110px]"
          sortField={sortField}
          sortDirection={sortDirection}
          disableSortInteractions={disableSortInteractions}
          onSortChange={onSortChange}
        />
        <SortableHeader
          field="exactVolume"
          label="Search Vol."
          tooltip={TT.searchVol}
          className="w-[110px]"
          sortField={sortField}
          sortDirection={sortDirection}
          disableSortInteractions={disableSortInteractions}
          onSortChange={onSortChange}
        />
        <SortableHeader
          field="asinDensity"
          label="ASIN Density"
          tooltip={TT.asinDensity}
          className="w-[110px]"
          sortField={sortField}
          sortDirection={sortDirection}
          disableSortInteractions={disableSortInteractions}
          onSortChange={onSortChange}
        />
        <SortableHeader
          field="competition"
          label="Competition"
          tooltip={TT.competition}
          className="w-[120px]"
          sortField={sortField}
          sortDirection={sortDirection}
          disableSortInteractions={disableSortInteractions}
          onSortChange={onSortChange}
        />
        <SortableHeader
          field="heroRelevanceScore"
          label="Rel."
          tooltip={TT.rel}
          className="w-[60px]"
          sortField={sortField}
          sortDirection={sortDirection}
          disableSortInteractions={disableSortInteractions}
          onSortChange={onSortChange}
        />
        {/* AUT-496: on both variants — the landscape tab renders this same table and its rows
            carry SERP Match values. `showThemeAndTags` still gates Theme and Tags below. */}
        {showSerpMatch && (
          <SortableHeader
            field="serpMatch"
            label="SERP Match"
            tooltip={TT.serpMatch}
            className="w-[90px]"
            sortField={sortField}
            sortDirection={sortDirection}
            disableSortInteractions={disableSortInteractions}
            onSortChange={onSortChange}
          />
        )}
        <SortableHeader
          field="searchVolumeTrend"
          label="Trend"
          tooltip={TT.trend}
          className="w-[84px] min-w-[84px]"
          sortField={sortField}
          sortDirection={sortDirection}
          disableSortInteractions={disableSortInteractions}
          onSortChange={onSortChange}
        />
        {showThemeAndTags && (
        <>
        <TableHead className="w-[115px] py-2.5 px-2 font-bold text-sm text-center whitespace-nowrap">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger
                className="cursor-help border-b border-dotted border-muted-foreground"
                onClick={(e) => e.preventDefault()}
              >
                Theme
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[280px]">
                {TT.theme}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TableHead>
        <TableHead className="w-[123px] py-2.5 px-2 font-bold text-sm text-center whitespace-nowrap">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger
                className="cursor-help border-b border-dotted border-muted-foreground"
                onClick={(e) => e.preventDefault()}
              >
                Utilization
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[280px]">
                {TT.utilization}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TableHead>
        <TableHead className="w-[138px] py-2.5 px-2 font-bold text-sm text-center whitespace-nowrap">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger
                className="cursor-help border-b border-dotted border-muted-foreground"
                onClick={(e) => e.preventDefault()}
              >
                Source
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[280px]">
                {TT.source}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </TableHead>
        </>
        )}
        {!readOnly && (
          <TableHead className="w-[70px] py-2.5 px-2 font-bold text-sm text-center whitespace-nowrap">
            Actions
          </TableHead>
        )}
      </TableRow>
    </TableHeader>
  );
}
